import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { useInfiniteList, makeApiFetcher } from './useInfiniteList.js';
import { useActivity } from './useActivity.js';

export interface LeadRow {
  id: string;
  contactId: number | null;
  businessName: string;
  category: string | null;
  address: string | null;
  city: string | null;
  zip: string | null;
  phone: string | null;
  googleMapsUrl: string | null;
  websiteUrl: string | null;
  rating: number | null;
  reviewCount: number | null;
  scrapedAt: string | null;
  status: string;
  websiteStatus: string | null;
  websiteIssuesJson: string | null;
  intelligenceJson: string | null;
  language: string | null;
  ownerName: string | null;
  ownerEmail: string | null;
  contactEmail: string | null;
  scrapedEmail: string | null;
  emailConfidence: string | null;
  emailSubject: string | null;
  emailBody: string | null;
  leadCategory: string | null;
  hasWebsite: number | null;
  hasEmail: number | null;
  pipelineStage: string;
  stageOverriddenByUser: number | null;
  auditScoreWave: number | null;
  auditScorePagespeedMobile: number | null;
  auditHasSsl: number | null;
  auditRunAt: string | null;
  stage1PreviewReady: number | null;
  draftedAt: string | null;
  sentAt: string | null;
  response: string | null;
  responseAt: string | null;
  nextAction: string | null;
  notes: string | null;
  updatedAt: string;
  priorityScore: number | null;
  analysisStatus: string | null;
  analyzedAt: string | null;
  techStack: string | null;
  techObsoleteScore: number | null;
  hasSsl: number | null;
  isResponsive: number | null;
  hasContactForm: number | null;
  logoUrl: string | null;
  websiteAgeYears: number | null;
  socialFacebook: string | null;
  socialInstagram: string | null;
  socialTwitter: string | null;
  socialLinkedin: string | null;
  socialYoutube: string | null;
  socialTiktok: string | null;
  enrichmentError: string | null;
  junkReason: string | null;
}

export interface LeadsResult {
  rows: LeadRow[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

export interface LeadFilters {
  search?: string;
  pipelineStage?: string;
  leadCategory?: string;
  city?: string;
  hasWebsite?: boolean;
  hasEmail?: boolean;
  hasDueDrafts?: boolean;
  excludeArchived?: boolean;
  page?: number;
  limit?: number;
  sortBy?: 'businessName' | 'city' | 'status' | 'updatedAt';
  sortDir?: 'asc' | 'desc';
}

export interface PipelineCount {
  stage: string;
  count: number;
}

function buildQuery(filters: LeadFilters): string {
  const params = new URLSearchParams();
  if (filters.search) params.set('search', filters.search);
  if (filters.pipelineStage) params.set('pipelineStage', filters.pipelineStage);
  if (filters.leadCategory) params.set('leadCategory', filters.leadCategory);
  if (filters.city) params.set('city', filters.city);
  if (filters.hasWebsite !== undefined) params.set('hasWebsite', String(filters.hasWebsite));
  if (filters.hasEmail !== undefined) params.set('hasEmail', String(filters.hasEmail));
  if (filters.hasDueDrafts) params.set('hasDueDrafts', 'true');
  if (filters.page) params.set('page', String(filters.page));
  if (filters.limit) params.set('limit', String(filters.limit));
  if (filters.sortBy) params.set('sortBy', filters.sortBy);
  if (filters.sortDir) params.set('sortDir', filters.sortDir);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export function useLeads(filters: LeadFilters = {}) {
  const { isActive } = useActivity();
  return useQuery<LeadsResult>({
    queryKey: ['leads', filters],
    queryFn: () => api.get<LeadsResult>(`/api/leads${buildQuery(filters)}`),
    // Poll while a pipeline run is in flight so the list reflects status
    // transitions (scrape → enrich → analyze → draft → sent) without needing
    // a manual refresh. Goes idle as soon as activity stops.
    refetchInterval: isActive ? 5_000 : false,
  });
}

export function useInfiniteLeads(filters: Omit<LeadFilters, 'page' | 'cursor'> = {}) {
  const { isActive } = useActivity();
  const params: Record<string, string | boolean | number | undefined> = {};
  if (filters.search) params['search'] = filters.search;
  if (filters.pipelineStage) params['pipelineStage'] = filters.pipelineStage;
  if (filters.leadCategory) params['leadCategory'] = filters.leadCategory;
  if (filters.city) params['city'] = filters.city;
  if (filters.hasWebsite !== undefined) params['hasWebsite'] = filters.hasWebsite;
  if (filters.hasEmail !== undefined) params['hasEmail'] = filters.hasEmail;
  if (filters.hasDueDrafts) params['hasDueDrafts'] = true;
  if (filters.excludeArchived) params['excludeArchived'] = true;
  if (filters.limit) params['limit'] = filters.limit;
  if (filters.sortBy) params['sortBy'] = filters.sortBy;
  if (filters.sortDir) params['sortDir'] = filters.sortDir;

  const fetchFn = makeApiFetcher<LeadRow>('/api/leads', params);

  return useInfiniteList<LeadRow>(
    ['leads', 'infinite', filters],
    fetchFn,
    {
      limit: filters.limit ?? 50,
      getTimestamp: (l) => l.scrapedAt ?? l.updatedAt,
      // Auto-refresh during pipeline activity: enrich/analyze/draft updates the
      // lead rows out-of-band and the list should reflect those without manual F5.
      refetchInterval: isActive ? 5_000 : false,
    },
  );
}

export function useFilteredLeadIds(filters: Omit<LeadFilters, 'page' | 'limit'>) {
  const params = new URLSearchParams();
  if (filters.search) params.set('search', filters.search);
  if (filters.pipelineStage) params.set('pipelineStage', filters.pipelineStage);
  if (filters.leadCategory) params.set('leadCategory', filters.leadCategory);
  if (filters.city) params.set('city', filters.city);
  if (filters.hasWebsite !== undefined) params.set('hasWebsite', String(filters.hasWebsite));
  if (filters.hasEmail !== undefined) params.set('hasEmail', String(filters.hasEmail));
  if (filters.hasDueDrafts) params.set('hasDueDrafts', 'true');
  const qs = params.toString();
  return useQuery<{ ids: string[] }>({
    queryKey: ['leads', 'filtered-ids', filters],
    queryFn: () => api.get<{ ids: string[] }>(`/api/leads/filtered-ids${qs ? `?${qs}` : ''}`),
    staleTime: 30_000,
  });
}

export function useLead(id: string | null) {
  return useQuery<LeadRow>({
    queryKey: ['lead', id],
    queryFn: () => api.get<LeadRow>(`/api/leads/${id}`),
    enabled: id !== null,
  });
}

export function usePipelineCounts() {
  return useQuery<PipelineCount[]>({
    queryKey: ['leads', 'pipeline'],
    queryFn: () => api.get<PipelineCount[]>('/api/leads/pipeline'),
  });
}

export function useDueDraftsCount() {
  return useQuery<{ count: number }>({
    queryKey: ['leads', 'due-count'],
    queryFn: () => api.get<{ count: number }>('/api/leads/due-count'),
    refetchInterval: 60_000,
  });
}

export function useUpdateLeadStage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, stage }: { id: string; stage: string }) =>
      api.patch<LeadRow>(`/api/leads/${id}/stage`, { stage }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['leads'] });
    },
  });
}

export function useUpdateLeadStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      api.patch<LeadRow>(`/api/leads/${id}/status`, { status }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['leads'] });
      void qc.invalidateQueries({ queryKey: ['lead', vars.id] });
    },
  });
}

export function useUpdateLead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Record<string, string>) =>
      api.put<LeadRow>(`/api/leads/${id}`, body),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['leads'] });
      void qc.invalidateQueries({ queryKey: ['lead', vars.id] });
    },
  });
}

export function useDeleteLead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<{ ok: boolean }>(`/api/leads/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['leads'] });
    },
  });
}

export function useBulkDeleteLeads() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => api.delete<{ deleted: number }>('/api/leads/bulk/delete', { ids }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['leads'] });
    },
  });
}

export function useRequalifyLead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ ok: boolean; qualified: boolean; pipelineStage?: string }>(`/api/leads/${id}/re-qualify`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['leads'] });
    },
  });
}

export function useBulkUpdateStage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, stage }: { ids: string[]; stage: string }) =>
      api.post<{ updated: number }>('/api/leads/bulk/stage', { ids, stage }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['leads'] });
    },
  });
}

export function useBulkArchive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) =>
      api.post<{ updated: number }>('/api/leads/bulk/archive', { ids }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['leads'] });
    },
  });
}

export function useReactivateLead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ ok: boolean }>(`/api/leads/${id}/reactivate`, {}),
    onSuccess: (_data, id) => {
      void qc.invalidateQueries({ queryKey: ['leads'] });
      void qc.invalidateQueries({ queryKey: ['lead', id] });
    },
  });
}

export function useBulkReactivate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) =>
      api.post<{ updated: number }>('/api/leads/bulk/reactivate', { ids }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['leads'] });
    },
  });
}

export function useRunLeadActions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, actions }: { id: string; actions: string[] }) =>
      api.post<{ ok: boolean; queued: string[] }>(`/api/leads/${id}/run-actions`, { actions }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['lead', vars.id] });
      void qc.invalidateQueries({ queryKey: ['lead-drafts', vars.id] });
      void qc.invalidateQueries({ queryKey: ['lead-analysis', vars.id] });
      void qc.invalidateQueries({ queryKey: ['leads'] });
    },
  });
}

export function useGenerateIntelligence() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<LeadRow>(`/api/leads/${id}/intelligence`, {}),
    onSuccess: (_data, id) => {
      void qc.invalidateQueries({ queryKey: ['leads'] });
      void qc.invalidateQueries({ queryKey: ['lead', id] });
    },
  });
}

export function useCreateGmailDraft(leadId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ ok: boolean; message: string }>(`/api/leads/${leadId}/gmail-draft`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['lead', leadId] }),
  });
}

export interface LeadAnalysis {
  analysisStatus: string | null;
  analyzedAt: string | null;
  businessSummary: string | null;
  opportunities: string[] | null;
  suggestedServices: string[] | null;
  priorityScore: number | null;
  proposalAngle: string | null;
  language: string | null;
  tokensInput: number | null;
  tokensOutput: number | null;
  costUsd: number | null;
}

export function useLeadAnalysis(id: string | null) {
  return useQuery<LeadAnalysis>({
    queryKey: ['lead-analysis', id],
    queryFn: () => api.get<LeadAnalysis>(`/api/leads/${id}/analysis`),
    enabled: id !== null,
  });
}

interface BulkActionResult {
  runId: number;
  eligibleCount: number;
  ignoredCount: number;
}

export function useBulkAnalyze() {
  return useMutation({
    mutationFn: (ids: string[]) =>
      api.post<BulkActionResult>('/api/leads/bulk/analyze', { ids }),
  });
}

export interface ConvertResult {
  action: 'created' | 'existing' | 'match_found';
  contactId: number;
  leadId?: string;
}

export function useConvertLeadToContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, forceNew }: { id: string; forceNew?: boolean }) => {
      const url = forceNew
        ? `/api/leads/${id}/convert-to-contact?force_new=true`
        : `/api/leads/${id}/convert-to-contact`;
      return api.post<ConvertResult>(url, {});
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['lead', vars.id] });
      void qc.invalidateQueries({ queryKey: ['leads'] });
      void qc.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
}

export function useLinkLeadToContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, contactId }: { id: string; contactId: number }) =>
      api.post<{ action: 'linked'; contactId: number }>(`/api/leads/${id}/link-to-contact`, { contactId }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['lead', vars.id] });
      void qc.invalidateQueries({ queryKey: ['leads'] });
      void qc.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
}

export interface BulkConvertResult {
  created: number;
  alreadyLinked: number;
  noEmail: number;
  matchFound: number;
}

export function useBulkConvertLeads() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) =>
      api.post<BulkConvertResult>('/api/leads/bulk/convert-to-contact', { ids }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['leads'] });
      void qc.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
}

export function useBulkDraft() {
  return useMutation({
    mutationFn: (ids: string[]) =>
      api.post<BulkActionResult>('/api/leads/bulk/draft', { ids }),
  });
}

export function useBulkSend() {
  return useMutation({
    mutationFn: (ids: string[]) =>
      api.post<BulkActionResult>('/api/leads/bulk/send', { ids }),
  });
}

export function useBulkJunk() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, reason }: { ids: string[]; reason?: string }) =>
      api.post<{ updated: number }>('/api/leads/bulk/junk', { ids, reason }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['leads'] });
    },
  });
}

export function useAnalyzeLead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<{ status: string }>(`/api/leads/${id}/analyze`, {}),
    onSuccess: (_data, id) => {
      void qc.invalidateQueries({ queryKey: ['lead-analysis', id] });
      void qc.invalidateQueries({ queryKey: ['leads'] });
    },
  });
}

export function useStartScrape() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ query, city, maxResults }: { query: string; city: string; maxResults?: number }) =>
      api.post<{ runId: number; status: string; message: string }>('/api/leads/scrape', {
        query,
        city,
        ...(maxResults !== undefined ? { maxResults } : {}),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['leads'] });
    },
  });
}

export interface CreateLeadInput {
  businessName: string;
  contactEmail?: string;
  phone?: string;
  websiteUrl?: string;
  city?: string;
  category?: string;
  address?: string;
  notes?: string;
}

export interface ReEnrichResult {
  status: 'found_website' | 'found_maps' | 'not_found' | 'skipped' | 'error';
  email?: string;
  phone?: string;
  method?: string;
}

export function useReEnrichLead(leadId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<ReEnrichResult>(`/api/leads/${leadId}/re-enrich`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['lead', leadId] });
      void qc.invalidateQueries({ queryKey: ['leads'] });
    },
  });
}

export function useCreateLead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateLeadInput) => api.post<LeadRow>('/api/leads', body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['leads'] });
      void qc.invalidateQueries({ queryKey: ['leads', 'pipeline'] });
    },
  });
}

export interface OutreachDraft {
  id: string;
  leadId: string;
  sequenceOrder: number;
  draftType: 'initial' | 'followup';
  subject: string;
  bodyHtml: string;
  bodyText: string;
  status: 'pending_review' | 'approved' | 'sent' | 'cancelled' | 'skipped' | 'paused' | 'failed';
  cancellationReason: string | null;
  sendError: string | null;
  sendAttemptedAt: number | null;
  scheduledFor: number | null;
  sentAt: number | null;
  sentMessageId: string | null;
  createdAt: number;
  updatedAt: number;
  isDue: number;
  generatedByClaude: number;
  claudeCostUsd: number | null;
}

export interface OutreachReply {
  id: string;
  leadId: string;
  messageId: string;
  matchedBy: string;
  matchConfidence: number | null;
  isVerified: number;
  receivedAt: number;
  direction: string | null;
  subject: string | null;
  bodySnippet: string | null;
  replyText: string | null;
  quoteText: string | null;
  fromDomain: string;
  fromAddress: string | null;
  toAddress: string | null;
  timestamp: string | null;
}

export function useLeadDrafts(id: string | null) {
  return useQuery<OutreachDraft[]>({
    queryKey: ['lead-drafts', id],
    queryFn: () => api.get<OutreachDraft[]>(`/api/leads/${id}/drafts`),
    enabled: id !== null,
  });
}

export function useGenerateDrafts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ ok: boolean; drafts: number; cost_usd: number }>(`/api/leads/${id}/draft`, {}),
    onSuccess: (_data, id) => {
      void qc.invalidateQueries({ queryKey: ['lead-drafts', id] });
    },
  });
}

export function useSendDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ leadId, draftId }: { leadId: string; draftId: string }) =>
      api.post<{ ok: boolean; messageId: string }>(`/api/leads/${leadId}/drafts/${draftId}/send`, {}),
    onSettled: (_data, _error, vars) => {
      void qc.invalidateQueries({ queryKey: ['lead-drafts', vars.leadId] });
      void qc.invalidateQueries({ queryKey: ['lead', vars.leadId] });
      void qc.invalidateQueries({ queryKey: ['leads'] });
    },
  });
}

export function useCancelDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ leadId, draftId }: { leadId: string; draftId: string }) =>
      api.post<{ ok: boolean }>(`/api/leads/${leadId}/drafts/${draftId}/cancel`, {}),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['lead-drafts', vars.leadId] });
    },
  });
}

export function useSkipDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ leadId, draftId }: { leadId: string; draftId: string }) =>
      api.post<{ ok: boolean }>(`/api/leads/${leadId}/drafts/${draftId}/skip`, {}),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['lead-drafts', vars.leadId] });
    },
  });
}

export function useUpdateDraft(leadId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ draftId, subject, body_text, body_html }: { draftId: string; subject?: string; body_text?: string; body_html?: string }) =>
      api.patch<OutreachDraft>(`/api/leads/${leadId}/drafts/${draftId}`, { subject, body_text, body_html }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['lead-drafts', leadId] });
    },
  });
}

export function usePauseOutreach() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (leadId: string) =>
      api.post<{ ok: boolean }>(`/api/leads/${leadId}/outreach/pause`, {}),
    onSuccess: (_data, leadId) => {
      void qc.invalidateQueries({ queryKey: ['lead-drafts', leadId] });
      void qc.invalidateQueries({ queryKey: ['lead', leadId] });
    },
  });
}

export function useResumeOutreach() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (leadId: string) =>
      api.post<{ ok: boolean }>(`/api/leads/${leadId}/outreach/resume`, {}),
    onSuccess: (_data, leadId) => {
      void qc.invalidateQueries({ queryKey: ['lead-drafts', leadId] });
      void qc.invalidateQueries({ queryKey: ['lead', leadId] });
    },
  });
}

export function useLeadReplies(leadId: string | null) {
  return useQuery<OutreachReply[]>({
    queryKey: ['lead-replies', leadId],
    queryFn: () => api.get<OutreachReply[]>(`/api/leads/${leadId}/replies`),
    enabled: leadId !== null,
  });
}

export function useVerifyReply() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ replyId, isRelevant }: { replyId: string; isRelevant: boolean; leadId: string }) =>
      api.post<{ ok: boolean }>(`/api/outreach-replies/${replyId}/verify`, { is_relevant: isRelevant }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['lead-replies', vars.leadId] });
      void qc.invalidateQueries({ queryKey: ['lead-drafts', vars.leadId] });
      void qc.invalidateQueries({ queryKey: ['lead', vars.leadId] });
      void qc.invalidateQueries({ queryKey: ['leads'] });
    },
  });
}

export function useSendReply(leadId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ replyId, bodyHtml, bodyText, attachments }: {
      replyId: string;
      bodyHtml: string;
      bodyText: string;
      attachments: File[];
    }) => {
      const fd = new FormData();
      fd.append('body_html', bodyHtml);
      fd.append('body_text', bodyText);
      for (const f of attachments) fd.append('attachments', f);
      return api.upload<{ ok: boolean; messageId: string }>(
        `/api/leads/${leadId}/replies/${replyId}/send-reply`,
        fd,
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['lead-drafts', leadId] });
      void qc.invalidateQueries({ queryKey: ['lead-replies', leadId] });
      void qc.invalidateQueries({ queryKey: ['lead', leadId] });
    },
  });
}

export function useSendLeadEmail(leadId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ subject, bodyHtml, bodyText, inReplyTo, references }: { subject: string; bodyHtml: string; bodyText: string; inReplyTo?: string; references?: string[] }) =>
      api.post<{ ok: boolean; messageId?: string }>(`/api/leads/${leadId}/compose-email`, { subject, bodyHtml, bodyText, ...(inReplyTo ? { inReplyTo, references: references ?? [inReplyTo] } : {}) }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['lead-replies', leadId] });
      void qc.invalidateQueries({ queryKey: ['lead', leadId] });
    },
  });
}
